/**
 * Reading a kitchen for staging: its counters (and which side of each is the
 * wall), its sinks and cooking units, its fridge, and what hangs over each
 * counter. Pure; no placement here.
 *
 * Facts from the corpus (30 kitchen-kind rooms, 2026-10-18) that shape this:
 *   - a countertop's long axis is normally local x with local -z at the wall,
 *     but 5 of 83 wall-backed corpus counters store the opposite yaw, so the
 *     wall side is MEASURED here (nearest wall to each long edge), never
 *     assumed;
 *   - 17 short slabs (corner fills, 0.5-24 in end pieces) are deeper than they
 *     are long; nothing leans on those;
 *   - ranges stand in the GAPS between slabs (0 of 19 inside one); cooktops
 *     sit ON a slab and are wall-parented, so the occupancy pass never sees
 *     them;
 *   - 80 of 120 uppers are wall-parented, which also hides them from the
 *     occupancy pass, while the other 40 were wrongly read as standing ON the
 *     counter. Overhead clearance is therefore its own measurement.
 */

const { objectTypeOf, isStagingDecor } = require('../../ownership');
const { footprintFor } = require('../../../geometry/objectFootprint');
const {
  doPolygonsOverlap,
} = require('../../../bathroom-analyzer/pipeline/geometry');
const {
  isFreestandingCountertop,
} = require('../../../cabinet-runs/countertopExposure');
const {
  objectShowsGlb,
} = require('../../../fixture-types/productRepresentation');
const {
  extractWallEndpoints,
} = require('../../../bathroom-analyzer/extraction');
const {
  pointToSegmentDistanceXZ,
} = require('../../../dorm-styling/dormDecorGeometry');
const {
  objectWorldFromLocal,
  objectLocalX,
} = require('../../../dorm-styling/plan/shared');
const {
  isBasinLike,
  longAxisHost,
  runIntervalFor,
  planDims,
} = require('../surfaces');
const { surfaceTopY } = require('../constants');
const { isPointInsidePolygon } = require('../../../geometry/pointInPolygon');

const IN = 0.0254;

const COUNTER_TYPES = new Set(['countertop', 'corner-fill-countertop']);
/** A range we can hang a towel on: it has a drawn oven door and handle. */
const RANGE_TYPES = new Set(['range']);
/** Cooking surfaces with no oven front: nothing may stand on them. A scanned
 * `stove` is a cooktop (~2 in tall in the corpus), not a range. */
const COOKTOP_TYPES = new Set(['cooktop', 'stove']);
const FRIDGE_TYPES = new Set(['refrigerator']);

/** A counter is wall-backed when its back edge is this close to a wall. */
const WALL_BACKED_M = 6 * IN;
/** Something this close to a counter's end closes it off. */
const END_TOUCH_M = 2 * IN;
/** Overhead objects are looked for this far above a counter. */
const OVERHEAD_REACH_M = 48 * IN;

/** Local z of a world point in an object's frame (the inverse of objectWorldFromLocal). */
function objectLocalZ(obj, point) {
  const rot = obj?.rotation?.y || 0;
  return (
    (point.x - obj.center.x) * Math.sin(rot) +
    (point.z - obj.center.z) * Math.cos(rot)
  );
}

const isCooktop = (o) => COOKTOP_TYPES.has(objectTypeOf(o));
const isRange = (o) => RANGE_TYPES.has(objectTypeOf(o));
const isCookingUnit = (o) => isRange(o) || isCooktop(o);

/** Every wall segment of the room, as [start, end] points. */
function wallSegments(room) {
  return (extractWallEndpoints(room) || [])
    .filter((e) => e?.start && e?.end)
    .map((e) => [e.start, e.end]);
}

const distanceToWalls = (point, segs) =>
  segs.length
    ? Math.min(...segs.map(([a, b]) => pointToSegmentDistanceXZ(point, a, b)))
    : Infinity;

/**
 * The counter as staging packs it: long axis local +x, and — when it is
 * against a wall — local -z turned to face that wall.
 */
/** One frame of a counter, its back the long edge nearer a wall. */
function measureFrame(host, segs) {
  const d = host.dimensions;
  const back = objectWorldFromLocal(host, 0, -d.z / 2);
  const front = objectWorldFromLocal(host, 0, d.z / 2);
  const dBack = distanceToWalls(back, segs);
  const dFront = distanceToWalls(front, segs);
  const flipped = dFront < dBack;
  const framed = flipped
    ? {
        ...host,
        rotation: {
          ...(host.rotation || {}),
          y: (host.rotation?.y || 0) + Math.PI,
        },
      }
    : host;
  return { framed, backToWall: Math.min(dBack, dFront) };
}

/**
 * Frame a counter: its run along local x, its back (local -z) at the wall.
 *
 * The STORED frame is asked first. A scan stores a slab with its run along x
 * and a z edge at the wall even when the slab is deeper than it is long:
 * Pittsburgh's 23.5 x 25 in slab beside its range has its stored back 0.0 in
 * from the wall, and framed by its LONG axis it read as a counter with no wall
 * at all, so the crock and mills stood mid-counter (Megan, 2026-10-21). Only
 * when neither stored z edge is at a wall is the frame derived from the long
 * axis, as before.
 *
 * @returns {{framed, backToWall, alongWall: boolean}} `alongWall` when the
 *   stored frame was the one at a wall
 */
function frameCounter(obj, segs) {
  const stored = measureFrame(obj, segs);
  if (stored.backToWall <= WALL_BACKED_M) return { ...stored, alongWall: true };
  return { ...measureFrame(longAxisHost(obj), segs), alongWall: false };
}

/**
 * What hangs over a counter, as stretches of its run: each with the clear
 * height from the counter top to the object's underside, and the band of the
 * counter's DEPTH it covers (an upper is ~12 in deep over a ~25 in counter, so
 * a prop at the front is not under it at all).
 */
function overheadOver(host, top, objects) {
  const hd = host.dimensions;
  const hostW = hd.x;
  const out = [];
  for (const o of objects) {
    if (!o?.center || !o?.dimensions || o === host) continue;
    const h = o.dimensions.y;
    if (!Number.isFinite(h)) continue;
    const bottom = o.center.y - h / 2;
    const clear = bottom - top;
    if (clear <= 1 * IN || clear > OVERHEAD_REACH_M) continue;
    const run = runIntervalFor(host, o, hostW);
    if (!run) continue;
    const w = Number.isFinite(o.dimensions.x)
      ? o.dimensions.x
      : o.dimensions.width;
    const dep = Number.isFinite(o.dimensions.z)
      ? o.dimensions.z
      : o.dimensions.depth;
    const corners = footprintFor(o.center, w, dep, o.rotation?.y || 0);
    if (!corners) continue;
    const zs = corners.map((c) => objectLocalZ(host, c));
    const zMin = Math.max(-hd.z / 2, Math.min(...zs));
    const zMax = Math.min(hd.z / 2, Math.max(...zs));
    if (zMax <= zMin) continue;
    out.push({ start: run.start, end: run.end, zMin, zMax, clear, id: o.id });
  }
  return out;
}

/**
 * Which run ENDS of a counter (-1 = its start, +1 = its end) are OPEN —
 * nothing against them: no wall, no other counter, nothing standing at counter
 * height (a range, a fridge, a tall cabinet). Lower cabinets sit below the top
 * and uppers above it, so neither closes an end. A prop at an open end is at
 * an exposed edge, like the front of the counter.
 */
function openEndsOf(framed, top, objects, segs, self) {
  const half = framed.dimensions.x / 2;
  const fp = (o) =>
    footprintFor(o.center, o.dimensions.x, o.dimensions.z, o.rotation?.y || 0);
  const standing = objects.filter((o) => {
    if (o === self) return false;
    const h = o.dimensions.y || 0;
    return (
      o.center.y + h / 2 >= top - 2 * IN && o.center.y - h / 2 <= top + 1 * IN
    );
  });
  return [-1, 1].filter((sx) => {
    const mid = objectWorldFromLocal(framed, sx * half, 0);
    if (
      segs.some(
        ([a, b]) => pointToSegmentDistanceXZ(mid, a, b) <= END_TOUCH_M + 0.1
      )
    )
      return false;
    // A strip just past the end, most of the counter's depth wide.
    const c = objectWorldFromLocal(framed, sx * (half + END_TOUCH_M / 2), 0);
    const strip = footprintFor(
      { x: c.x, z: c.z },
      END_TOUCH_M,
      framed.dimensions.z * 0.8,
      framed.rotation?.y || 0
    );
    return !standing.some((o) => {
      const f = fp(o);
      return f && doPolygonsOverlap(strip, f);
    });
  });
}

/**
 * The kitchen as staging sees it.
 * @returns {{
 *   counters: Array<{obj, host, hostW, top, isIsland, wallBacked, overhead, openEnds}>,
 *   sinks: Object[], ranges: Object[], hangableRanges: Object[],
 *   cooktops: Object[], fridges: Object[], segs: Array,
 * }}
 */
function readKitchen(room) {
  const objects = (room?.objects || []).filter(
    (o) => o && o.center && o.dimensions && !isStagingDecor(o)
  );
  const segs = wallSegments(room);
  const counters = [];
  for (const obj of objects) {
    if (!COUNTER_TYPES.has(objectTypeOf(obj))) continue;
    if (!(obj.dimensions.x > 0) || !(obj.dimensions.z > 0)) continue;
    const isIsland = isFreestandingCountertop(obj, {
      objects: room.objects,
      cabinetRuns: room.cabinetRuns,
    });
    const { framed, backToWall, alongWall } = isIsland
      ? { framed: longAxisHost(obj), backToWall: Infinity, alongWall: false }
      : frameCounter(obj, segs);
    const top = surfaceTopY(framed);
    if (top == null) continue;
    // Wall-backed when the STORED frame is at a wall (whatever its
    // proportions), or, framed by its long axis, when that axis is the stored
    // run: a short end piece framed the other way round has a side edge for a
    // "back".
    const isLongways = (obj.dimensions.x || 0) >= (obj.dimensions.z || 0);
    const wallBacked =
      !isIsland && (alongWall || isLongways) && backToWall <= WALL_BACKED_M;
    counters.push({
      obj,
      host: framed,
      hostW: framed.dimensions.x,
      top,
      isIsland,
      wallBacked,
      overhead: overheadOver(framed, top, objects),
      openEnds: openEndsOf(framed, top, objects, segs, obj),
    });
  }
  const ranges = objects.filter(isRange);
  return {
    counters,
    sinks: objects.filter((o) => isBasinLike(o)),
    ranges,
    // Only a range whose drawn front we know: `objectShowsGlb` is false (no
    // catalog model, or the procedural opt-out). A catalog model's handle
    // could be anywhere, so its towel goes beside the sink instead.
    hangableRanges: ranges.filter((r) => !objectShowsGlb(r)),
    cooktops: objects.filter(isCooktop),
    fridges: objects.filter((o) => FRIDGE_TYPES.has(objectTypeOf(o))),
    segs,
  };
}

/** Where along a counter's run (0..hostW) a world point projects. */
const runPosition = (counter, point) =>
  objectLocalX(counter.host, point) + counter.hostW / 2;

/** Distance in plan from a world point to a counter's run (0 when beside it). */
function distanceAlongRun(counter, point) {
  const t = runPosition(counter, point);
  if (t < 0) return -t;
  if (t > counter.hostW) return t - counter.hostW;
  return 0;
}

/** Plan distance between two world points. */
const planDistance = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);

/**
 * A counter's slab END nearest a world point, as {t, point}: where a prop
 * beside a range stands is the end of the slab the range abuts.
 */
function nearestEnd(counter, point) {
  const t = runPosition(counter, point) < counter.hostW / 2 ? 0 : counter.hostW;
  const p = objectWorldFromLocal(counter.host, t - counter.hostW / 2, 0);
  return { t, point: { x: p.x, z: p.z } };
}

/**
 * The point on a counter's run nearest a world point, as {t, point}. For a
 * range standing in the gap at a slab's end this is that end (`nearestEnd`);
 * for a slab running ALONGSIDE the range — 50 Park's 26 in end cap — it is
 * the stretch beside the range, where `nearestEnd` picked an open end and
 * stood the crock 1.5 in from it.
 */
function closestOnRun(counter, point) {
  const t = Math.min(Math.max(runPosition(counter, point), 0), counter.hostW);
  const p = objectWorldFromLocal(counter.host, t - counter.hostW / 2, 0);
  return {
    t,
    point: { x: p.x, z: p.z },
    atEnd: t <= 1e-6 || t >= counter.hostW - 1e-6,
  };
}

/** Is this object's footprint touching or within `gap` of the counter's? */
function nearCounter(counter, obj, gap) {
  const run = runIntervalFor(counter.host, obj, counter.hostW);
  const z = objectLocalZ(counter.host, obj.center);
  const halfDepth = counter.host.dimensions.z / 2;
  const reach = Math.max(planDims(obj).x || 0, planDims(obj).z || 0) / 2;
  if (Math.abs(z) > halfDepth + reach + gap) return false;
  if (run) return true;
  return distanceAlongRun(counter, obj.center) <= reach + gap;
}

/** Counters whose axes are this close to square meet at a corner. */
const CORNER_MAX_COS = 0.3;
/** Scan slop allowed where one slab's end butts another's face. */
const CORNER_BUTT_M = 3 * IN;

/**
 * The CORNERS of a kitchen's L and U runs: where one counter's END butts
 * into another counter's long face, at that counter's end. The square they
 * share belongs to the counter that runs through it — the corner a stager
 * fills, because it is too deep to work in (research in
 * `reference-docs/staging/overview.md`, "Balance").
 *
 * @returns {Array<{counter, t, size, point}>} the counter that owns the square,
 *   the run position of the square's centre, its side along that run, and
 *   its centre in plan
 */
function counterCorners(counters) {
  const axis = (c) => {
    const a = objectWorldFromLocal(c.host, 0, 0);
    const b = objectWorldFromLocal(c.host, 1, 0);
    return { x: b.x - a.x, z: b.z - a.z };
  };
  const out = [];
  for (const c of counters) {
    if (c.isIsland) continue;
    const ca = axis(c);
    // The square is the counter's own end: a slab shorter than it is a stub
    // beside the corner, not the counter running through it.
    const owns = (size) => c.hostW >= size - CORNER_BUTT_M;
    for (const d of counters) {
      if (d === c || d.isIsland || Math.abs(d.top - c.top) > 1 * IN) continue;
      const da = axis(d);
      if (Math.abs(ca.x * da.x + ca.z * da.z) > CORNER_MAX_COS) continue;
      const size = d.host.dimensions.z;
      if (!owns(size)) continue;
      for (const sx of [-1, 1]) {
        // The centre of d's end edge.
        const p = objectWorldFromLocal(d.host, (sx * d.hostW) / 2, 0);
        const lz = objectLocalZ(c.host, p);
        if (Math.abs(Math.abs(lz) - c.host.dimensions.z / 2) > CORNER_BUTT_M)
          continue;
        const lx = objectLocalX(c.host, p);
        for (const end of [-1, 1]) {
          const inward = end * ((end * c.hostW) / 2 - lx);
          if (Math.abs(inward - size / 2) > CORNER_BUTT_M) continue;
          const t = c.hostW / 2 + end * (c.hostW / 2 - size / 2);
          if (out.some((k) => k.counter === c && Math.abs(k.t - t) < size / 2))
            continue;
          const q = objectWorldFromLocal(c.host, t - c.hostW / 2, 0);
          out.push({ counter: c, t, size, point: { x: q.x, z: q.z } });
        }
      }
    }
  }
  return out;
}

/** Slabs whose footprints come this close are one continuous stretch of counter. */
const STRETCH_TOUCH_M = 1 * IN;

/**
 * The length of the continuous stretch of counter each counter belongs to:
 * slabs that touch, end to end or at a corner, add up. Islands (seating
 * ledges included) are left out — a peninsula's bar ledge touches every slab
 * around it and would make the whole kitchen one stretch.
 *
 * @returns {Map<counter, number>} metres
 */
function counterStretches(counters) {
  const grown = (c) =>
    footprintFor(
      c.host.center,
      c.host.dimensions.x + 2 * STRETCH_TOUCH_M,
      c.host.dimensions.z + 2 * STRETCH_TOUCH_M,
      c.host.rotation?.y || 0
    );
  const runs = counters.filter((c) => !c.isIsland);
  const fps = new Map(runs.map((c) => [c, grown(c)]));
  const out = new Map();
  for (const c of runs) {
    if (out.has(c)) continue;
    const group = [c];
    for (let i = 0; i < group.length; i++)
      for (const d of runs)
        if (
          !group.includes(d) &&
          fps.get(d) &&
          fps.get(group[i]) &&
          doPolygonsOverlap(fps.get(group[i]), fps.get(d))
        )
          group.push(d);
    const length = group.reduce((sum, g) => sum + g.hostW, 0);
    for (const g of group) out.set(g, length);
  }
  return out;
}

/**
 * How far a point stands from an object's rotated footprint, in plan: 0 when
 * it is over it, Infinity when the object has no box.
 */
function planGapToObject(point, o) {
  const f = footprintFor(
    o.center,
    o.dimensions.x,
    o.dimensions.z || 0,
    o.rotation?.y || 0
  );
  if (!f) return Infinity;
  if (isPointInsidePolygon(point, f)) return 0;
  return Math.min(
    ...f.map((a, i) =>
      pointToSegmentDistanceXZ(point, a, f[(i + 1) % f.length])
    )
  );
}

module.exports = {
  readKitchen,
  planGapToObject,
  counterCorners,
  counterStretches,
  runPosition,
  distanceAlongRun,
  nearestEnd,
  closestOnRun,
  nearCounter,
  planDistance,
  objectLocalZ,
  isCooktop,
  isRange,
  isCookingUnit,
  COUNTER_TYPES,
  WALL_BACKED_M,
};
